import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  BugIcon,
  CommentDiscussionIcon,
  LightBulbIcon,
  PaperAirplaneIcon,
  XIcon,
} from "@primer/octicons-react";
import type { ComponentType } from "react";
import { addDoc, collection, doc } from "firebase/firestore";
import { db } from "../config/firebase";
import { useAuth } from "../contexts/AuthContext";

interface FeedbackModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSuccess?: () => void;
}

interface FeedbackType {
  label: string;
  value: string;
  description: string;
  icon: ComponentType<{ size?: number }>;
}

const FEEDBACK_TYPES: FeedbackType[] = [
  {
    label: "General feedback",
    value: "Feedback",
    description: "Share an experience or suggestion",
    icon: CommentDiscussionIcon,
  },
  {
    label: "Feature request",
    value: "Feature Request",
    description: "Tell us what would help your workflow",
    icon: LightBulbIcon,
  },
  {
    label: "Bug report",
    value: "Bug Report",
    description: "Let us know when something is broken",
    icon: BugIcon,
  },
];

export function FeedbackModal({
  isOpen,
  onDismiss,
  onSuccess,
}: FeedbackModalProps) {
  const [feedbackType, setFeedbackType] = useState(FEEDBACK_TYPES[0].value);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!isOpen) return;

    document.body.classList.add("app-modal-open");

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isSubmitting) onDismiss();
    };

    document.addEventListener("keydown", handleKeyDown);
    window.requestAnimationFrame(() => textAreaRef.current?.focus());
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("app-modal-open");
    };
  }, [isOpen, isSubmitting, onDismiss]);

  const handleSubmit = async () => {
    if (!feedbackText.trim() || !user || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError("");
    try {
      await addDoc(collection(db, "feedback"), {
        feedback_type: feedbackType,
        feedback_text: feedbackText.trim(),
        user_ref: doc(db, "users", user.uid),
        user_email: user.email,
        created_at: new Date(),
      });

      setFeedbackType(FEEDBACK_TYPES[0].value);
      setFeedbackText("");
      onDismiss();
      onSuccess?.();
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setSubmitError("We couldn’t send your feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="feedback-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) onDismiss();
      }}
    >
      <section
        className="feedback-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-dialog-title"
        aria-describedby="feedback-dialog-description"
      >
        <header className="feedback-dialog-header">
          <div className="feedback-dialog-title-wrap">
            <span className="feedback-dialog-icon"><CommentDiscussionIcon size={19} /></span>
            <div>
              <h2 id="feedback-dialog-title">Help us improve ForgePDF</h2>
              <p id="feedback-dialog-description">Your feedback goes directly to our product team.</p>
            </div>
          </div>
          <button
            type="button"
            className="feedback-close"
            aria-label="Close feedback dialog"
            onClick={onDismiss}
            disabled={isSubmitting}
          >
            <XIcon size={17} />
          </button>
        </header>

        <div className="feedback-dialog-body">
          <fieldset className="feedback-type-fieldset">
            <legend>What would you like to share?</legend>
            <div className="feedback-type-grid" role="radiogroup" aria-label="Feedback type">
              {FEEDBACK_TYPES.map((type) => {
                const Icon = type.icon;
                const active = feedbackType === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={active ? "feedback-type-card is-active" : "feedback-type-card"}
                    onClick={() => setFeedbackType(type.value)}
                  >
                    <span className="feedback-type-icon"><Icon size={17} /></span>
                    <span>
                      <strong>{type.label}</strong>
                      <small>{type.description}</small>
                    </span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="feedback-message-field">
            <div className="feedback-message-label">
              <label htmlFor="feedback-message">Tell us more</label>
              <span>{feedbackText.length}/1000</span>
            </div>
            <textarea
              ref={textAreaRef}
              id="feedback-message"
              value={feedbackText}
              onChange={(event) => setFeedbackText(event.target.value)}
              maxLength={1000}
              rows={6}
              placeholder="What happened, or what would make ForgePDF better for you?"
            />
            <p>A few details help us understand and act on your feedback faster.</p>
          </div>

          {submitError && <div className="feedback-submit-error" role="alert">{submitError}</div>}
        </div>

        <footer className="feedback-dialog-footer">
          <span>Thank you for helping us build a better product.</span>
          <div>
            <button type="button" className="feedback-secondary-button" onClick={onDismiss} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="button"
              className="feedback-primary-button"
              onClick={handleSubmit}
              disabled={!feedbackText.trim() || isSubmitting || !user}
            >
              <PaperAirplaneIcon size={15} />
              {isSubmitting ? "Sending…" : "Send feedback"}
            </button>
          </div>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
